import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LUCKY_DRAW_PRIZES, TIER_CONFIG } from './luckyDrawConfig';
import { weightedRandomPick } from './luckyDrawUtils';
import { recordLuckyDraw } from '../../../api/luckyDraw';
import { useAuth } from '../../../contexts/AuthContext';
import './LuckyDrawModal.scss';

/**
 * 抽籤 Modal 元件
 * 包含完整的抽籤動畫流程
 * 使用 Portal 渲染到 body，避免被父層 CSS 影響
 */
const LuckyDrawModal = ({ onClose }) => {
  const [stage, setStage] = useState('entering'); // 'entering' | 'shaking' | 'drawing' | 'result'
  const [prize, setPrize] = useState(null);
  const modalRef = useRef(null);
  const { user, profile, viewingCompanyId } = useAuth();

  // 處理 ESC 鍵關閉
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && stage === 'result') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [stage, onClose]);

  // 開啟時給 body 加 class，關閉時移除
  useEffect(() => {
    document.body.classList.add('lucky-draw-active');
    return () => {
      document.body.classList.remove('lucky-draw-active');
    };
  }, []);

  // 動畫流程控制
  useEffect(() => {
    let timer;

    switch (stage) {
      case 'entering':
        // 入場動畫 1 秒後進入搖籤階段
        timer = setTimeout(() => setStage('shaking'), 1000);
        break;

      case 'shaking':
        // 搖籤 2.5 秒
        timer = setTimeout(async () => {
          // 抽籤邏輯
          const selectedPrize = weightedRandomPick(LUCKY_DRAW_PRIZES);
          setPrize(selectedPrize);
          
          // 記錄抽籤結果到資料庫
          if (user && profile) {
            await recordLuckyDraw({
              userId: user.id,
              userName: profile.display_name || profile.name || profile.email,
              userEmail: profile.email,
              prizeName: selectedPrize.name,
              prizeMessage: selectedPrize.message,
              prizeTier: selectedPrize.tier,
              companyId: viewingCompanyId || profile.company_id,
              departmentId: profile.department_id
            });
          }
          
          setStage('drawing');
        }, 2500);
        break;

      case 'drawing':
        // 籤飛出動畫 1.5 秒
        timer = setTimeout(() => setStage('result'), 1500);
        break;

      default:
        break;
    }

    return () => clearTimeout(timer);
  }, [stage]);

  // 點擊背景關閉（僅在結果階段）
  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current && stage === 'result') {
      onClose();
    }
  };

  // 根據稀有度渲染特效
  const renderTierEffect = () => {
    if (!prize || stage !== 'result') return null;

    const tierConfig = TIER_CONFIG[prize.tier];

    switch (prize.tier) {
      case 'SSR':
        return (
          <div className="tier-effect ssr-effect">
            <div className="golden-rays"></div>
            <div className="golden-particles">
              {[...Array(30)].map((_, i) => (
                <div key={i} className="particle" />
              ))}
            </div>
          </div>
        );

      case 'SR':
        return (
          <div className="tier-effect sr-effect">
            <div className="red-fireworks">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="firework-particle" />
              ))}
            </div>
          </div>
        );

      case 'R':
        return (
          <div className="tier-effect r-effect">
            <div className="sparkles">
              {[...Array(15)].map((_, i) => (
                <div key={i} className="sparkle" />
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const modalContent = (
    <div 
      ref={modalRef}
      className={`lucky-draw-modal ${stage}`}
      onClick={handleBackdropClick}
    >
      {/* 籤桶 */}
      <div className={`lottery-tube ${stage}`}>
        {/* 籤條（搖籤階段顯示在桶內，抽籤階段飛出）- 放在 SVG 前面，讓桶口遮住籤 */}
        {(stage === 'shaking' || stage === 'drawing') && (
          <div className={`lottery-stick ${stage}`}>
            <div className="stick-body"></div>
          </div>
        )}

        <svg viewBox="0 0 200 300" className="tube-svg">
          {/* 籤桶主體 */}
          <defs>
            <linearGradient id="tubeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#8B0000" />
              <stop offset="50%" stopColor="#DC143C" />
              <stop offset="100%" stopColor="#8B0000" />
            </linearGradient>
            <filter id="tubeShadow">
              <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#000" floodOpacity="0.5"/>
            </filter>
          </defs>
          
          {/* 桶身 */}
          <rect x="50" y="80" width="100" height="180" rx="10" 
                fill="url(#tubeGradient)" filter="url(#tubeShadow)" />
          
          {/* 金色裝飾條 */}
          <rect x="45" y="75" width="110" height="8" rx="4" fill="#D4AF37" />
          <rect x="45" y="255" width="110" height="8" rx="4" fill="#D4AF37" />
          
          {/* 桶口 */}
          <ellipse cx="100" cy="80" rx="55" ry="15" fill="#8B0000" />
          <ellipse cx="100" cy="80" rx="50" ry="12" fill="#000" opacity="0.3" />
          
          {/* 福字 */}
          <text x="100" y="180" fontSize="48" fill="#FFD700" 
                textAnchor="middle" fontWeight="bold">福</text>
        </svg>
      </div>

      {/* 結果顯示 */}
      {stage === 'result' && prize && (
        <div className="result-display">
          {/* 稀有度特效 */}
          {renderTierEffect()}

          {/* 展開的籤條 */}
          <div className={`result-card ${prize.tier.toLowerCase()}`}>
            <div className="card-header">
              <span className="tier-label">{TIER_CONFIG[prize.tier].label}</span>
            </div>
            <div className="card-body">
              <h2 className="prize-name">{prize.name}</h2>
              <p className="prize-message">{prize.message}</p>
            </div>
            <div className="card-footer">
              <button 
                className="close-button" 
                onClick={(e) => {
                  e.stopPropagation(); // 阻止事件冒泡
                  onClose();
                }}
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // 使用 Portal 渲染到 body
  return createPortal(modalContent, document.body);
};

export default LuckyDrawModal;
